import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  where,
} from 'firebase/firestore';
import { Shield, RefreshCw, Search, Users, History, ArrowLeft, LineChart, Download } from 'lucide-react';
import { useAuthContext } from '../../contexts/AuthContext';
import { db } from '../../services/firebase';
import { resolveIsAdmin } from '../../utils/adminAccess';
import {
  ADMIN_CLIENT_TRADES_QUERY_LIMIT,
  ADMIN_TRADE_HISTORY_START,
  adminTradeHistoryStartDate,
} from '../../utils/adminTradeHistoryWindow';
import AdminTradierHistoryPanel, { formatMoney, pnlClass } from './AdminTradierHistoryPanel';
import AdminClientPaymentsCards from './AdminClientPaymentsCards';
import useAdminTradierHistory from './useAdminTradierHistory';
import { isTrackedSymbol } from '../../utils/adminTradeSymbols';
import { reconcilePlatformTrades } from '../../utils/reconcilePlatformTrades';
import { jtncTradeLedger } from '../../utils/qfaaTradierPnl';
import useJtncPnlInputs from './useJtncPnlInputs';
import { jtncLedgerTitle, jtncUnavailableHint, jtncUnavailableTitle } from './jtncLedgerText';
import { takeoverKind, TAKEOVER_TITLE } from '../../utils/clientTakeover';

const HISTORY_SOURCES = [
  { id: 'platform', label: 'Platform log' },
  { id: 'tradier', label: 'Tradier (actual)' },
];

const formatPnl = (value) => {
  const n = Number(value) || 0;
  const abs = Math.abs(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (n > 0) return `+$${abs}`;
  if (n < 0) return `-$${abs}`;
  return `$${abs}`;
};

const toDate = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDateTime = (value) => {
  const d = toDate(value);
  if (!d) return '—';
  // Pinned to Eastern: fill times only make sense against the market clock, and
  // an admin's browser timezone silently shifting them is how executions get
  // mismatched during support calls.
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/New_York',
    timeZoneName: 'short',
  });
};

const csvTimeFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/New_York',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const csvDateTime = (value) => {
  const d = toDate(value);
  return d ? `${csvTimeFormatter.format(d).replace(',', '')} ET` : '';
};

const csvCell = (v) => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const legsLabel = (raw) => {
  const option = raw?.entry?.option;
  if (Array.isArray(option?.legs) && option.legs.length) {
    return option.legs.map((l) => `${l.side} ${l.quantity} ${l.optionSymbol}`).join(' / ');
  }
  return option?.optionSymbol ? `${option.side || ''} ${option.optionSymbol}`.trim() : '';
};

const clientLabel = (user) =>
  user.username || user.displayName || user.email || user.id;

const brokerLabel = (broker) =>
  broker && broker !== '—' ? broker.charAt(0).toUpperCase() + broker.slice(1) : 'Unknown broker';

const accountKeyOf = (trade) => `${trade.broker}|${trade.accountId}`;

const newestFirst = (a, b) => {
  const aTime = toDate(a.exitAt)?.getTime() || toDate(a.entryAt)?.getTime() || 0;
  const bTime = toDate(b.exitAt)?.getTime() || toDate(b.entryAt)?.getTime() || 0;
  return bTime - aTime;
};

const isCompletedTrade = (data) =>
  data.tradeType === 'completed' ||
  data.lifecycle?.phase === 'completed' ||
  Boolean(data.lifecycle?.exit && data.lifecycle?.entry) ||
  Boolean(data.exit?.filledAt);


/** Test / non-client orders and rows left off the desk's trade master never count toward JTNC P&L. */
const jtncExclusionOf = (raw) => {
  if (raw?.test_trade === true) return 'Test / non-client trade';
  if (raw?.jtnc_pnl_excluded === true) {
    return raw.jtnc_pnl_exclusion?.reason === 'echo_in_master' ? 'Echo on desk list' : 'Not on desk list';
  }
  return null;
};

const TradierAccountCards = ({ history, pnlCards, jtncLedger, jtncInputsError }) => {
  const { data, loading, error } = history;
  if (error && !pnlCards) {
    return (
      <div className="mt-3 text-xs text-red-300/90">
        Tradier numbers unavailable: {error}
      </div>
    );
  }
  const balances = data?.balances;
  const summary = data?.summary;
  const lifetime = data?.lifetime;
  const placeholder = loading && !data ? '…' : null;
  const since = ADMIN_TRADE_HISTORY_START.toLocaleString('en-US', { month: 'short', year: 'numeric' });
  const tradierPnlCards = [
    {
      label: `JTNC Trade P&L since ${since}`,
      value: jtncLedger ? jtncLedger.total : null,
      placeholder: jtncInputsError ? '—' : summary && !jtncLedger ? '…' : null,
      hint: jtncInputsError ? jtncUnavailableHint(jtncInputsError) : 'JTNC trades + credits/bonuses + Pro subscriptions',
      hintClass: jtncInputsError ? 'text-amber-400/80' : null,
      title: jtncInputsError ? jtncUnavailableTitle(jtncInputsError) : jtncLedgerTitle(jtncLedger, since),
    },
    {
      label: 'Account P&L (lifetime)',
      value: lifetime?.accountPnl,
      hint:
        lifetime?.netDeposits != null
          ? `Account value − net deposits ${formatMoney(lifetime.netDeposits, { signed: false })}`
          : 'Account value − net deposits',
      title: 'Tradier total_equity minus net deposits since the account opened (wires, ACH, checks and account transfers). Every trade, fee, credit, interest and dividend is in this number.',
    },
  ];

  const cards = [
    {
      label: 'Cash balance',
      value: balances?.totalCash,
      unsigned: true,
      hint: 'Tradier cash — same figure as Balance on Client Settings',
      title: 'Tradier total_cash. Matches the Balance column on the Client Settings page.',
    },
    {
      label: 'Account value',
      value: balances?.totalEquity,
      unsigned: true,
      hint:
        balances?.openPositionsValue != null
          ? `Incl. open positions ${formatMoney(balances.openPositionsValue, { signed: false })}`
          : 'Cash + open positions',
      title: 'Tradier total_equity: cash plus the market value of open positions.',
    },
    ...(pnlCards || tradierPnlCards),
  ];

  return (
    <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
      {cards.map((c) => (
        <div key={c.label} className="bg-gray-800 rounded-lg px-3 py-2" title={c.title}>
          <div className="text-gray-500">{c.label}</div>
          <div
            className={`text-base font-semibold tabular-nums ${
              c.unsigned || c.value == null ? 'text-gray-200' : pnlClass(c.value)
            }`}
          >
            {(c.placeholder ?? placeholder) ?? formatMoney(c.value, { signed: !c.unsigned })}
          </div>
          {c.hint && <div className={`text-[10px] mt-0.5 ${c.hintClass || 'text-gray-500'}`}>{c.hint}</div>}
        </div>
      ))}
    </div>
  );
};

const AdminClientTradeHistory = () => {
  const { user, userProfile, loading: authLoading } = useAuthContext();
  const [searchParams] = useSearchParams();
  const clientFromUrl = (searchParams.get('client') || '').trim();

  // The data below is gated by Firestore rules on the qfaaOperator custom claim
  // (the desk's provisioning mechanism — no prod user carries isAdmin/role doc
  // fields, so a doc-field-only gate would deny every real operator AND rest on
  // fields that were client-forgeable until the #237 rules guard). role==='admin'
  // stays as a strict secondary for Admin-SDK-provisioned doc admins.
  const [operatorClaim, setOperatorClaim] = useState(null); // null = unresolved
  useEffect(() => {
    let cancelled = false;
    if (!user) { setOperatorClaim(false); return undefined; }
    user.getIdTokenResult()
      .then((r) => { if (!cancelled) setOperatorClaim(r.claims?.qfaaOperator === true); })
      .catch(() => { if (!cancelled) setOperatorClaim(false); });
    return () => { cancelled = true; };
  }, [user]);

  const gateResolved = !authLoading && operatorClaim !== null;
  const isAdmin = resolveIsAdmin({ user, userProfile, operatorClaim });

  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [clientsError, setClientsError] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClientId, setSelectedClientId] = useState(clientFromUrl || null);
  const [historySource, setHistorySource] = useState(
    searchParams.get('source') === 'platform' ? 'platform' : 'tradier'
  );

  useEffect(() => {
    if (clientFromUrl) setSelectedClientId(clientFromUrl);
  }, [clientFromUrl]);

  const [trades, setTrades] = useState([]);
  const [tradesLoading, setTradesLoading] = useState(false);
  const [tradesError, setTradesError] = useState(null);
  const [tradesTruncated, setTradesTruncated] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  // 'connected' = the Tradier account the stats and P&L cards are verified against.
  const [accountFilter, setAccountFilter] = useState('connected');

  useEffect(() => {
    setAccountFilter('connected');
  }, [selectedClientId]);
  const tradierHistory = useAdminTradierHistory(
    isAdmin && operatorClaim === true ? selectedClientId : null
  );

  // Live client list
  useEffect(() => {
    if (!isAdmin || !db) return undefined;

    setClientsLoading(true);
    setClientsError(null);

    const unsubscribe = onSnapshot(
      collection(db, 'users'),
      (snapshot) => {
        const rows = snapshot.docs.map((docSnap) => {
          const data = docSnap.data() || {};
          return {
            id: docSnap.id,
            username: data.username || '',
            displayName: data.displayName || data.name || '',
            email: data.email || '',
            status: data.status || '',
            tier: data.tier || '',
            updatedAt: data.updatedAt || data.lastLogin || null,
          };
        });

        rows.sort((a, b) =>
          clientLabel(a).localeCompare(clientLabel(b), undefined, { sensitivity: 'base' })
        );

        setClients(rows);
        setClientsLoading(false);
        setLastUpdatedAt(new Date());
      },
      (err) => {
        console.error('Admin client list listener failed:', err);
        setClientsError('Failed to load clients — see console for details');
        setClientsLoading(false);
      }
    );

    return unsubscribe;
  }, [isAdmin]);

  // Live trade history for selected client
  useEffect(() => {
    if (!isAdmin || !db || !selectedClientId) {
      setTrades([]);
      setTradesLoading(false);
      setTradesError(null);
      setTradesTruncated(false);
      return undefined;
    }

    // Other clients' backlog rows require qfaaOperator (firestore.rules).
    // Email allowlist alone opens the shell but cannot read their ledger.
    if (operatorClaim !== true) {
      setTrades([]);
      setTradesLoading(false);
      setTradesTruncated(false);
      setTradesError(
        'Client trade history needs the qfaaOperator claim. Run scripts/grant-qfaa-operator.mjs, then sign out and back in.'
      );
      return undefined;
    }

    setTradesLoading(true);
    setTradesError(null);
    setTradesTruncated(false);

    const historyStart = adminTradeHistoryStartDate();
    const historyStartTs = Timestamp.fromDate(historyStart);

    const mapRows = (snapshot) =>
      snapshot.docs
        .map((docSnap) => {
          const data = docSnap.data() || {};
          return {
            id: docSnap.id,
            symbol: data.symbol || data.underlying || '—',
            broker: data.broker || data.brokerType || '—',
            accountId: data.accountId || data.account || '—',
            tradeType: data.tradeType || data.lifecycle?.phase || '—',
            side: data.entry?.side || data.side || '—',
            quantity: data.entry?.quantity || data.quantity || data.size || '—',
            entryAt: data.entry?.filledAt || data.entryFilledAt || null,
            exitAt: data.exit?.filledAt || data.exitFilledAt || null,
            netPnl: data.performance?.netPnl ?? data.netPnl ?? 0,
            createdAt: data.createdAt || data.created_at || null,
            raw: data,
          };
        })
        .filter((row) => {
          const when =
            toDate(row.exitAt) ||
            toDate(row.entryAt) ||
            toDate(row.createdAt);
          // Keep undated rows (schema drift) but drop clearly pre-window trades.
          if (when && when < historyStart) return false;
          if (!isTrackedSymbol(row.symbol)) return false;
          return (
            isCompletedTrade(row.raw) ||
            row.tradeType === 'entry_only' ||
            row.tradeType === 'cancelled'
          );
        })
        .sort(newestFirst);

    // Dated window uses the existing userId + createdAt composite index.
    const datedQuery = query(
      collection(db, 'tbl_user_order_backlogs'),
      where('userId', '==', selectedClientId),
      where('createdAt', '>=', historyStartTs),
      orderBy('createdAt', 'asc'),
      limit(ADMIN_CLIENT_TRADES_QUERY_LIMIT)
    );
    const fallbackQuery = query(
      collection(db, 'tbl_user_order_backlogs'),
      where('userId', '==', selectedClientId),
      limit(ADMIN_CLIENT_TRADES_QUERY_LIMIT)
    );

    let unsub = null;
    let usingFallback = false;

    const attach = (q, isFallback) => {
      usingFallback = isFallback;
      let serverSynced = false;
      unsub = onSnapshot(
        q,
        { includeMetadataChanges: true },
        (snapshot) => {
          // The admin dashboard's collection-wide listener streams the same docs
          // into the shared cache; a cache-first snapshot here held only the
          // client's oldest rows and showed that subset as their Realized P&L.
          if (snapshot.metadata.fromCache && !serverSynced) return;
          serverSynced = true;
          setTrades(mapRows(snapshot));
          setTradesTruncated(snapshot.size >= ADMIN_CLIENT_TRADES_QUERY_LIMIT);
          setTradesLoading(false);
          setLastUpdatedAt(new Date());
        },
        (err) => {
          console.error('Admin trade history listener failed:', err);
          const msg = err?.message || '';
          if (!usingFallback && /index|requires an index/i.test(msg)) {
            if (typeof unsub === 'function') unsub();
            attach(fallbackQuery, true);
            return;
          }
          setTradesError('Failed to load trade history — see console for details');
          setTradesLoading(false);
        }
      );
    };

    attach(datedQuery, false);
    return () => {
      if (typeof unsub === 'function') unsub();
    };
  }, [isAdmin, operatorClaim, selectedClientId]);

  const filteredClients = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) => {
      const haystack = `${c.username} ${c.displayName} ${c.email} ${c.id}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [clients, clientSearch]);

  const selectedClient = useMemo(
    () => clients.find((c) => c.id === selectedClientId) || null,
    [clients, selectedClientId]
  );

  const tradierAccountId = tradierHistory.data?.accountId || null;
  const tradierEvents = tradierHistory.data?.events || null;

  // Rows logged against a client's earlier broker account can't be checked
  // against the connected account's history and skew its totals.
  const accountTrades = useMemo(
    () => (tradierAccountId ? trades.filter((t) => String(t.accountId) === String(tradierAccountId)) : trades),
    [trades, tradierAccountId]
  );

  const jtncInputs = useJtncPnlInputs(isAdmin && operatorClaim === true ? selectedClientId : null, tradierAccountId);
  const jtncLedger = useMemo(
    () => (tradierEvents && !jtncInputs.loading && !jtncInputs.error ? jtncTradeLedger(tradierEvents, jtncInputs) : null),
    [tradierEvents, jtncInputs]
  );

  const reconciliation = useMemo(() => {
    if (!tradierEvents) return null;
    return reconcilePlatformTrades(
      accountTrades.map((t) => ({ id: t.id, raw: t.raw, completed: isCompletedTrade(t.raw) })),
      tradierEvents
    );
  }, [accountTrades, tradierEvents]);

  // Completed rows with no fills on the Tradier account were not executed
  // through QFAA (the client traded it themselves, or it never filled).
  const visibleTrades = useMemo(() => {
    if (!reconciliation) return accountTrades;
    return accountTrades.filter((t) => {
      if (!isCompletedTrade(t.raw)) return true;
      const status = reconciliation.byId.get(t.id)?.status;
      return status === 'matched' || status === 'pending';
    });
  }, [accountTrades, reconciliation]);

  const otherAccountTrades = useMemo(
    () => (tradierAccountId ? trades.filter((t) => String(t.accountId) !== String(tradierAccountId)) : []),
    [trades, tradierAccountId]
  );

  const otherAccounts = useMemo(() => {
    const byKey = new Map();
    otherAccountTrades.forEach((t) => {
      const key = accountKeyOf(t);
      const entry = byKey.get(key) || { key, broker: t.broker, accountId: t.accountId, count: 0 };
      entry.count += 1;
      byKey.set(key, entry);
    });
    return [...byKey.values()].sort((a, b) => b.count - a.count);
  }, [otherAccountTrades]);

  const showingOtherAccounts = accountFilter !== 'connected' && otherAccounts.length > 0;

  const shownTrades = useMemo(() => {
    if (!showingOtherAccounts) return visibleTrades;
    if (accountFilter === 'all') return [...visibleTrades, ...otherAccountTrades].sort(newestFirst);
    return otherAccountTrades.filter((t) => accountKeyOf(t) === accountFilter);
  }, [showingOtherAccounts, accountFilter, visibleTrades, otherAccountTrades]);

  const tradeSummary = useMemo(() => {
    let completed = 0;
    let winners = 0;
    let losers = 0;
    let loggedPnl = 0;
    let pendingPnl = 0;
    let pending = 0;
    let excluded = 0;
    let takenOverPnl = 0;
    visibleTrades.forEach((t) => {
      if (!isCompletedTrade(t.raw)) return;
      if (jtncExclusionOf(t.raw)) {
        excluded += 1;
        return;
      }
      completed += 1;
      const logged = Number(t.netPnl) || 0;
      loggedPnl += logged;
      const match = reconciliation?.byId.get(t.id);
      let pnl = logged;
      if (match?.status === 'matched') {
        pnl = match.tradierPnl;
      } else {
        pending += 1;
        pendingPnl += logged;
      }
      if (pnl > 0) winners += 1;
      else if (pnl < 0) losers += 1;
      if (takeoverKind(t.raw)) takenOverPnl += pnl;
    });
    const round2 = (n) => Math.round(n * 100) / 100;
    return {
      completed,
      winners,
      losers,
      winRate: completed ? Math.round((winners / completed) * 1000) / 10 : null,
      loggedPnl,
      pendingPnl: round2(pendingPnl),
      pending,
      excluded,
      clientManaged: accountTrades.filter((t) => takeoverKind(t.raw)).length,
      // Taken-over trades stay in JTNC P&L (Chris, 2026-10-21); this is their share of it.
      takenOverPnl: round2(takenOverPnl),
      notOnTradier: accountTrades.length - visibleTrades.length,
      otherAccountRows: trades.length - accountTrades.length,
    };
  }, [trades, accountTrades, visibleTrades, reconciliation]);

  const qfaaPnlCards = useMemo(() => {
    const since = ADMIN_TRADE_HISTORY_START.toLocaleString('en-US', { month: 'short', year: 'numeric' });
    const verifying = (tradierHistory.loading && !tradierEvents) || (tradierEvents && !jtncLedger && !jtncInputs.error);
    const placeholder = jtncInputs.error ? '—' : verifying ? '…' : null;
    const listHint = jtncInputs.error ? jtncUnavailableHint(jtncInputs.error) : null;
    const tradierPnl = jtncLedger ? jtncLedger.total : null;
    const currentPnl = tradierPnl == null ? null : Math.round((tradierPnl + tradeSummary.pendingPnl) * 100) / 100;
    return [
      {
        label: `JTNC QFAA Trade P&L since ${since}`,
        value: tradierPnl,
        placeholder,
        hint: listHint || (tradierEvents ? null : 'Tradier unavailable'),
        hintClass: listHint ? 'text-amber-400/80' : null,
        title: jtncInputs.error ? jtncUnavailableTitle(jtncInputs.error) : jtncLedgerTitle(jtncLedger, since),
      },
      {
        label: 'JTNC QFAA Current P&L',
        value: tradierEvents ? currentPnl : tradeSummary.loggedPnl,
        placeholder,
        hint: listHint || (tradierEvents
          ? 'Includes Tradier QFAA P&L plus JTNC QFAA P&L for today'
          : 'Unverified — Tradier unavailable'),
        hintClass: listHint || !tradierEvents ? 'text-amber-400/80' : null,
        title: tradierEvents
          ? `JTNC QFAA Trade P&L ${formatPnl(tradierPnl)} plus ${formatPnl(tradeSummary.pendingPnl)} JTNC-logged P&L on ${tradeSummary.pending} trade${tradeSummary.pending === 1 ? '' : 's'} Tradier has not posted yet.`
          : 'As logged by the platform; Tradier fills could not be loaded.',
      },
    ];
  }, [tradierEvents, tradeSummary, tradierHistory.loading, jtncLedger, jtncInputs.error]);

  const exportPlatformCsv = () => {
    const head = [
      'Entry (ET)', 'Exit (ET)', 'Symbol', 'Legs', 'Type', 'Qty', 'Entry price', 'Exit price',
      'Broker', 'Account', 'JTNC P&L', 'Tradier P&L', 'Difference', 'Status', 'Group ID',
    ];
    const rows = shownTrades.map((t) => {
      const raw = t.raw || {};
      const logged = Number(t.netPnl) || 0;
      const match = reconciliation?.byId.get(t.id);
      const verified = match?.status === 'matched';
      let status = 'not reconciled';
      if (!isCompletedTrade(raw)) status = t.tradeType;
      else if (verified) status = 'matched';
      else if (match) status = 'waiting for Tradier';
      return [
        csvDateTime(t.entryAt), csvDateTime(t.exitAt), t.symbol, legsLabel(raw), t.tradeType, t.quantity,
        raw.entry?.price ?? '', raw.exit?.price ?? '', t.broker, t.accountId,
        isCompletedTrade(raw) ? logged : '',
        verified ? match.tradierPnl : '',
        verified ? Math.round((logged - match.tradierPnl) * 100) / 100 : '',
        status, raw.group_id || '',
      ];
    });
    const blob = new Blob(['\ufeff' + [head, ...rows].map((r) => r.map(csvCell).join(',')).join('\n')], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const name = selectedClient ? clientLabel(selectedClient) : selectedClientId;
    a.href = url;
    a.download = `platform_log_${String(name).replace(/[^\w.-]+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Resolve the gate before rendering a verdict — without this, an operator sees
  // a flash of "Access Denied" while the profile/claim load.
  if (!gateResolved) {
    return (
      <div className="min-h-screen bg-[#0b0d2a] p-8 flex items-center justify-center text-gray-400">
        <RefreshCw className="h-5 w-5 animate-spin mr-2" />
        Checking access...
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#0b0d2a] p-8">
        <div className="bg-red-950/40 border border-red-500/40 rounded-lg p-6 max-w-xl">
          <div className="flex items-center gap-2 text-red-300">
            <Shield className="h-5 w-5" />
            <p>Access Denied: Admin privileges required</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b0d2a] text-white p-4 md:p-8">
      <div className="max-w-[1800px] mx-auto space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <Link
                to="/admin"
                className="inline-flex items-center gap-1 text-sm text-purple-300 hover:text-purple-200"
              >
                <ArrowLeft className="h-4 w-4" />
                Admin Dashboard
              </Link>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold">Client Trade History</h1>
            <p className="text-sm text-gray-400 mt-1">
              Live client list and trade history from{' '}
              {ADMIN_TRADE_HISTORY_START.toLocaleString('en-US', { month: 'long', year: 'numeric' })}.
              Updates automatically.
              {lastUpdatedAt ? ` Last update: ${lastUpdatedAt.toLocaleTimeString()}` : ''}
            </p>
          </div>
          {clientsError || tradesError ? (
            <div className="flex items-center gap-2 text-xs text-red-300/90 bg-red-950/30 border border-red-700/40 rounded-full px-3 py-1">
              <span className="h-2 w-2 rounded-full bg-red-400" />
              Stream error
            </div>
          ) : (
            <div className="flex items-center gap-2 text-xs text-emerald-300/90 bg-emerald-950/30 border border-emerald-700/40 rounded-full px-3 py-1">
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
              Live
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:h-[calc(100vh-9rem)] min-h-[70vh]">
          {/* Client list */}
          <aside className="lg:col-span-3 bg-gray-900/70 border border-gray-700 rounded-xl overflow-hidden flex flex-col min-h-0 max-h-[70vh] lg:max-h-none">
            <div className="p-4 border-b border-gray-700 space-y-3 shrink-0">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold flex items-center gap-2">
                  <Users className="h-4 w-4 text-purple-300" />
                  Clients
                </h2>
                <span className="text-xs text-gray-400">{filteredClients.length}</span>
              </div>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  placeholder="Search username, email, id..."
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-purple-500"
                />
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain [scrollbar-gutter:stable] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-600 [&::-webkit-scrollbar-track]:bg-gray-900/80">
              {clientsLoading && (
                <div className="p-6 text-center text-gray-400 text-sm">
                  <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" />
                  Loading clients...
                </div>
              )}
              {clientsError && (
                <div className="p-4 text-sm text-red-300">{clientsError}</div>
              )}
              {!clientsLoading && !clientsError && filteredClients.length === 0 && (
                <div className="p-6 text-sm text-gray-500 text-center">No clients found</div>
              )}
              {filteredClients.map((client) => {
                const active = client.id === selectedClientId;
                return (
                  <button
                    key={client.id}
                    type="button"
                    onClick={() => setSelectedClientId(client.id)}
                    className={`w-full text-left px-4 py-3 border-b border-gray-800 hover:bg-gray-800/70 transition-colors ${
                      active ? 'bg-purple-950/40 border-l-2 border-l-purple-400' : ''
                    }`}
                  >
                    <div className="font-medium text-sm truncate">{clientLabel(client)}</div>
                    <div className="text-xs text-gray-400 truncate mt-0.5">
                      {client.email || client.id}
                    </div>
                    <div className="flex gap-2 mt-1 text-[10px] uppercase tracking-wide">
                      {client.status && (
                        <span className="text-cyan-300/90">{client.status}</span>
                      )}
                      {client.tier && (
                        <span className="text-purple-300/90">{client.tier}</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </aside>

          {/* Trade history */}
          <section className="lg:col-span-9 bg-gray-900/70 border border-gray-700 rounded-xl overflow-hidden flex flex-col min-h-0">
            <div className="p-4 border-b border-gray-700">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="font-semibold flex items-center gap-2">
                    <History className="h-4 w-4 text-purple-300" />
                    Trade History
                    {tradeSummary.clientManaged > 0 && (
                      <span
                        className="text-red-500 font-bold"
                        title={`Client Managed QFAA Trades (${tradeSummary.clientManaged})`}
                      >
                        *
                      </span>
                    )}
                  </h2>
                  {tradeSummary.clientManaged > 0 && (
                    <p className="text-[11px] text-red-400 mt-0.5">
                      * Client Managed QFAA Trades ({tradeSummary.clientManaged}) · P&amp;L{' '}
                      <span className="tabular-nums">{formatPnl(tradeSummary.takenOverPnl)}</span>
                    </p>
                  )}
                  {selectedClient ? (
                    <p className="text-sm text-gray-400 mt-1">
                      {clientLabel(selectedClient)}
                      <span className="text-gray-600"> · </span>
                      <span className="font-mono text-xs">{selectedClient.id}</span>
                    </p>
                  ) : (
                    <p className="text-sm text-gray-500 mt-1">Select a client to view trades</p>
                  )}
                </div>
                {selectedClient && (
                  <div className="flex flex-wrap items-center gap-3">
                    <Link
                      to={`/admin/client-performance?userId=${encodeURIComponent(selectedClient.id)}&email=${encodeURIComponent(selectedClient.email || '')}`}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-purple-950/50 border border-purple-500/40 text-purple-200 hover:bg-purple-900/50"
                    >
                      <LineChart className="h-3.5 w-3.5" />
                      Performance chart
                    </Link>
                    <div className="inline-flex rounded-lg border border-gray-700 overflow-hidden text-xs" role="tablist" aria-label="History source">
                      {HISTORY_SOURCES.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          role="tab"
                          aria-selected={historySource === s.id}
                          onClick={() => setHistorySource(s.id)}
                          className={`px-3 py-2 font-semibold ${
                            historySource === s.id
                              ? 'bg-purple-600/80 text-white'
                              : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                          }`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                    {historySource === 'platform' && (
                    <div className="flex gap-3 text-xs">
                      {[
                        { label: '# QFAA Trades', value: tradeSummary.completed, title: 'Completed QFAA trades' },
                        { label: '# Winners', value: tradeSummary.winners, className: 'text-emerald-400' },
                        { label: '# Losers', value: tradeSummary.losers, className: 'text-red-400' },
                        {
                          label: 'Win Rate',
                          value: tradeSummary.winRate == null ? '—' : `${tradeSummary.winRate.toFixed(1)}%`,
                          title: '# Winners / # QFAA Trades × 100',
                        },
                      ].map((s) => (
                        <div key={s.label} className="bg-gray-800 rounded-lg px-3 py-2" title={s.title}>
                          <div className="text-gray-500">{s.label}</div>
                          <div className={`font-semibold tabular-nums ${s.className || ''}`}>{s.value}</div>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={exportPlatformCsv}
                        disabled={!shownTrades.length}
                        className="self-center inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 hover:bg-gray-700 disabled:opacity-40"
                      >
                        <Download className="h-3.5 w-3.5" /> CSV
                      </button>
                    </div>
                    )}
                  </div>
                )}
              </div>
              {selectedClientId && (
                <TradierAccountCards
                  history={tradierHistory}
                  jtncLedger={jtncLedger}
                  jtncInputsError={jtncInputs.error}
                  pnlCards={historySource === 'platform' ? qfaaPnlCards : null}
                />
              )}
              {selectedClient && (
                <AdminClientPaymentsCards
                  email={selectedClient.email}
                  jtncPnl={jtncLedger ? jtncLedger.total : null}
                  jtncError={jtncInputs.error}
                />
              )}
            </div>

            <div className="flex-1 min-h-0 overflow-auto max-h-[60vh] lg:max-h-none">
              {selectedClientId && historySource === 'tradier' ? (
                <AdminTradierHistoryPanel
                  key={selectedClientId}
                  userId={selectedClientId}
                  clientName={selectedClient ? clientLabel(selectedClient) : selectedClientId}
                  history={tradierHistory}
                  jtncLedger={jtncLedger}
                  jtncInputsError={jtncInputs.error}
                />
              ) : (
              <>
              {tradesTruncated && (
                <div className="m-3 p-3 rounded-lg bg-amber-950/40 border border-amber-600/40 text-amber-200 text-xs">
                  This client has more rows than the{' '}
                  {ADMIN_CLIENT_TRADES_QUERY_LIMIT.toLocaleString()}-row view limit since October
                  2025. The rows below may be incomplete for this client.
                </div>
              )}
              {!selectedClientId && (
                <div className="p-10 text-center text-gray-500 text-sm">
                  Choose a client on the left to stream their trade history.
                </div>
              )}
              {selectedClientId && tradesLoading && (
                <div className="p-10 text-center text-gray-400 text-sm">
                  <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" />
                  Loading trades...
                </div>
              )}
              {tradesError && (
                <div className="p-4 text-sm text-red-300">{tradesError}</div>
              )}
              {selectedClientId && !tradesLoading && !tradesError && otherAccounts.length > 0 && (
                <div className="mx-3 mt-3 flex flex-wrap items-center gap-2 text-xs">
                  <label htmlFor="admin-trade-account" className="text-gray-400">Account</label>
                  <select
                    id="admin-trade-account"
                    value={accountFilter}
                    onChange={(e) => setAccountFilter(e.target.value)}
                    className="bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 focus:outline-none focus:border-purple-500"
                  >
                    <option value="connected">
                      Tradier {tradierAccountId} · verified ({visibleTrades.length})
                    </option>
                    {otherAccounts.map((a) => (
                      <option key={a.key} value={a.key}>
                        {brokerLabel(a.broker)} {a.accountId} · logged only ({a.count})
                      </option>
                    ))}
                    <option value="all">
                      All accounts ({visibleTrades.length + otherAccountTrades.length})
                    </option>
                  </select>
                </div>
              )}
              {showingOtherAccounts && (
                <div className="m-3 p-3 rounded-lg bg-amber-950/40 border border-amber-600/40 text-amber-200 text-xs">
                  Rows from accounts other than Tradier {tradierAccountId} show P&amp;L as logged by the platform,
                  not verified with the broker. The stats and P&amp;L cards above cover Tradier {tradierAccountId} only.
                </div>
              )}
              {selectedClientId && !tradesLoading && !tradesError && shownTrades.length === 0 && (
                <div className="p-10 text-center text-gray-500 text-sm">
                  No trades found for this client.
                </div>
              )}
              {selectedClientId && !tradesLoading && (tradeSummary.otherAccountRows > 0 || tradeSummary.notOnTradier > 0 || tradeSummary.pending > 0 || tradeSummary.excluded > 0) && (
                <div className="m-3 p-3 rounded-lg bg-gray-800/60 border border-gray-700 text-gray-300 text-xs space-y-1">
                  {tradeSummary.excluded > 0 && (
                    <div>
                      {tradeSummary.excluded} completed row{tradeSummary.excluded === 1 ? '' : 's'} marked{' '}
                      <span className="px-1 rounded bg-gray-700 text-[10px]">excluded</span> (test / non-client
                      trade, or not on the desk&apos;s trade list) {tradeSummary.excluded === 1 ? 'is' : 'are'} not
                      counted in the stats or JTNC P&amp;L.
                    </div>
                  )}
                  {tradeSummary.otherAccountRows > 0 && !showingOtherAccounts && (
                    <div>
                      {tradeSummary.otherAccountRows} rows logged against other broker accounts are not shown
                      (connected Tradier account {tradierAccountId}); pick them in Account above.
                    </div>
                  )}
                  {tradeSummary.notOnTradier > 0 && (
                    <div>
                      {tradeSummary.notOnTradier} completed row{tradeSummary.notOnTradier === 1 ? '' : 's'} with no
                      matching fills on Tradier {tradeSummary.notOnTradier === 1 ? 'is' : 'are'} hidden — not
                      executed through QFAA.
                    </div>
                  )}
                  {reconciliation && tradeSummary.pending > 0 && (
                    <div>
                      {tradeSummary.pending} trade{tradeSummary.pending === 1 ? ' is' : 's are'} waiting for Tradier
                      to post the session&apos;s fills (next day); JTNC QFAA Current P&amp;L counts{' '}
                      {tradeSummary.pending === 1 ? 'it' : 'them'} at the JTNC-logged P&amp;L until then.
                    </div>
                  )}
                </div>
              )}
              {selectedClientId && shownTrades.length > 0 && (
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-900 border-b border-gray-700 text-left text-xs text-gray-400 uppercase tracking-wide">
                    <tr>
                      <th className="px-3 py-2 font-medium">Exit / Entry</th>
                      <th className="px-3 py-2 font-medium">Symbol</th>
                      <th className="px-3 py-2 font-medium">Type</th>
                      <th className="px-3 py-2 font-medium">Qty</th>
                      <th className="px-3 py-2 font-medium">Broker</th>
                      <th className="px-3 py-2 font-medium text-right">Net P&amp;L</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shownTrades.map((trade) => {
                      const loggedOnly = Boolean(tradierAccountId) && String(trade.accountId) !== String(tradierAccountId);
                      const logged = Number(trade.netPnl) || 0;
                      const match = reconciliation?.byId.get(trade.id);
                      const verified = match?.status === 'matched';
                      const pnl = verified ? match.tradierPnl : logged;
                      const drift = verified ? Math.round((logged - match.tradierPnl) * 100) / 100 : 0;
                      return (
                        <tr key={trade.id} className="border-b border-gray-800/80 hover:bg-gray-800/40">
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-300">
                            <div>{formatDateTime(trade.exitAt)}</div>
                            <div className="text-gray-500">in {formatDateTime(trade.entryAt)}</div>
                          </td>
                          <td className="px-3 py-2 font-medium">
                            {trade.symbol}
                            {takeoverKind(trade.raw) && (
                              <span className="text-red-500 font-bold ml-0.5" title={TAKEOVER_TITLE[takeoverKind(trade.raw)]}>*</span>
                            )}
                            {trade.raw?.exit_price_unavailable === true && (
                              <span
                                className="ml-1 px-1 rounded bg-amber-900/60 text-[10px] text-amber-200 font-normal"
                                title="Exit price unavailable: the broker's close price could not be read"
                              >
                                no exit price
                              </span>
                            )}
                            {jtncExclusionOf(trade.raw) && (
                              <span
                                className="ml-1 px-1 rounded bg-gray-700 text-[10px] text-gray-300 font-normal"
                                title={`${jtncExclusionOf(trade.raw)} — not counted in JTNC P&L`}
                              >
                                excluded
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-400">{trade.tradeType}</td>
                          <td className="px-3 py-2 text-xs">{trade.quantity}</td>
                          <td className="px-3 py-2 text-xs text-gray-400">
                            <div>{trade.broker}</div>
                            <div className="text-gray-600 truncate max-w-[120px]">{trade.accountId}</div>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {match && !verified && isCompletedTrade(trade.raw) ? (
                              <>
                                <div className="font-medium text-gray-400">{formatPnl(logged)}</div>
                                <div className="text-[10px] text-amber-400/80">waiting for Tradier</div>
                              </>
                            ) : (
                              <>
                                <div className={`font-medium ${pnlClass(pnl)}`}>{formatPnl(pnl)}</div>
                                {verified && drift !== 0 && (
                                  <div className="text-[10px] text-gray-500">JTNC {formatPnl(logged)}</div>
                                )}
                                {loggedOnly && isCompletedTrade(trade.raw) && (
                                  <div className="text-[10px] text-amber-400/80">logged · not verified</div>
                                )}
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              </>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AdminClientTradeHistory;
